/**
 * Centralized property URL and link utility — the single source of truth for all
 * customer-facing property navigation.
 *
 * Exports a three-tier helper suite:
 *  - `getPropertyUrl`  → string | null  — programmatic routing, template literals, href values
 *  - `PropertyLink`    → React.ReactElement — SPA navigation via React Router (found)
 *  - `PropertyAnchor`  → React.ReactElement — native <a> for react-intl rich text
 *
 * Scope (current phase): customer base property URLs only — `/properties/:uuid`.
 * Admin URLs (`/admin/properties/:uuid`) and sub-routes are intentionally out of scope.
 */

import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * @typedef {object} PropertyUrlInput
 * @property {string} uuid - Property UUID.
 * @property {object} [query] - Optional query params. Values may be strings, booleans,
 *   numbers, or Luxon DateTime instances (normalized to ISO date strings automatically).
 */

/**
 * Duck-type check for Luxon DateTime-like values.
 * Returns `true` when the value is non-null and exposes a `toISODate` method,
 * which is the minimum interface required for ISO date string normalization.
 * @param {*} value - Value to inspect.
 * @returns {boolean} `true` if the value has a `toISODate` method, `false` otherwise.
 */
const isIsoDateLike = (value) => value && typeof value.toISODate === 'function';

/**
 * Build a base customer property URL string with optional query parameters.
 *
 * - Returns `null` if `uuid` is falsy.
 * - Omits query entries whose value is `null`, `undefined`, or empty string.
 * - Luxon `DateTime` values are normalized via `.toISODate()`.
 *
 * Prefer `PropertyLink` or `PropertyAnchor` for rendering navigable elements.
 * Use this directly only when a raw string is required (e.g. programmatic
 * `router.push`, string interpolation, or passing `href` to a non-React context).
 * @param {PropertyUrlInput} input - Inputs.
 * @returns {string|null} Relative URL string, or `null` when `uuid` is missing.
 */
export const buildPropertyUrl = ({ uuid, query } = {}) => {
  if (!uuid) {
    return null;
  }

  const basePath = `/properties/${uuid}`;

  if (!query) {
    return basePath;
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    const normalizedValue = isIsoDateLike(value) ? value.toISODate() : value;
    params.set(key, String(normalizedValue));
  });

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Render a React Router `<Link>` to a customer property page.
 *
 * Use this for standard SPA navigation where full page reloads must be avoided.
 * The `to` prop is computed from `getPropertyUrl` so all query-parameter
 * normalisation rules (Luxon DateTime → ISO date, empty-value omission) apply
 * automatically.
 * @param {object} props - Component props.
 * @param {string} props.uuid - Property UUID; renders `null` when falsy.
 * @param {React.ReactNode} props.children - Link label content.
 * @param {object} [props.query] - Optional query parameters forwarded to `getPropertyUrl`.
 * @returns {React.ReactElement|null} A `<Link>` element, or `null` when `uuid` is missing.
 */
export function PropertyLink({ uuid = null, children, query = null }) {
  const to = buildPropertyUrl({ uuid, query });
  if (!to) {
    return null;
  }
  return <Link to={to}>{children}</Link>;
}

PropertyLink.propTypes = {
  uuid: PropTypes.string,
  children: PropTypes.node.isRequired,
  query: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

/**
 * Render a native `<a>` anchor to a customer property page.
 *
 * Use this instead of `PropertyLink` specifically when the element must be
 * injected into a `react-intl` `<FormattedMessage>` rich-text value, where
 * React Router `<Link>` components can conflict with the intl rendering context.
 *
 * Previously, this rendering logic lived inline inside `buildPropertyReference` in
 * `src/helpers/tradeHelpers.jsx`. That helper remains as a trade-specific
 * adapter (trade-point traversal, popover branching) and delegates the anchor
 * rendering to this component.
 * @param {object} props - Component props.
 * @param {string} props.uuid - Property UUID; renders `null` when falsy.
 * @param {React.ReactNode} props.children - Anchor label content.
 * @param {object} [props.query] - Optional query parameters forwarded to `getPropertyUrl`.
 * @returns {React.ReactElement|null} An `<a>` element, or `null` when `uuid` is missing.
 */
export function PropertyAnchor({ uuid = null, children, query = null }) {
  const href = buildPropertyUrl({ uuid, query });
  if (!href) {
    return null;
  }
  return <a href={href}>{children}</a>;
}

PropertyAnchor.propTypes = {
  uuid: PropTypes.string,
  children: PropTypes.node.isRequired,
  query: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};
